/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import { type RegionItem, NO_REGION_ITEMS, useRegionItems, withComponentData } from '@/components/region-items';
import { SectionHeader } from '@/components/section-header';
import { cn } from '@/lib/utils';

const splitLayoutDefaults = {
    columnLayout: '30-70',
    hideOnDesktop: false,
    hideOnMobile: false,
    marginTop: 32,
    marginBottom: 32,
} as const;

/** PD does not validate ranges either: clamp authored margins to something sane. */
const MAX_MARGIN = 200;

type ColumnId = 'narrow' | 'wide';

/** Column order and desktop track per layout. Literal class names so Tailwind can see them. */
const LAYOUTS = {
    '30-70': { order: ['narrow', 'wide'], gridClass: 'md:grid-cols-[minmax(0,3fr)_minmax(0,7fr)]' },
    '70-30': { order: ['wide', 'narrow'], gridClass: 'md:grid-cols-[minmax(0,7fr)_minmax(0,3fr)]' },
} as const satisfies Record<string, { order: readonly ColumnId[]; gridClass: string }>;

type ColumnLayout = keyof typeof LAYOUTS;

/** Component types that take the `embedded` prop. Others may spread unknown props onto the DOM. */
const EMBEDDABLE_TYPES = new Set(['priceshoes_layout.carousel']);

/** Each column item fills its cell, so e.g. a banner matches the carousel's height. */
const ITEM_CLASS = 'flex-1';

@Component('splitLayout', {
    name: 'Split Layout (30/70)',
    description:
        'Section header plus two columns: a narrow one (30%) and a wide one (70%), e.g. a promo banner next to a product carousel. Stacked on mobile.',
    group: 'priceshoes_layout',
})
@RegionDefinition([
    {
        id: 'narrow',
        name: 'Narrow Column (30%)',
        description: 'Add one component, e.g. a promo banner.',
        maxComponents: 1,
        // Literal list: the cartridge generator reads decorators statically. Add new content types here.
        componentTypeInclusions: [
            'Content.announcementBanner',
            'Content.contentCard',
            'Content.hero',
            'Content.pdImage',
            'Content.popularCategory',
            'Content.productRecommendations',
            'Content.productTile',
            'priceshoes_content.banner',
            'priceshoes_content.catalogCard',
            'priceshoes_content.ctaCard',
            'priceshoes_content.imageCard',
            'priceshoes_content.splitHero',
            'priceshoes_layout.carousel',
        ],
    },
    {
        id: 'wide',
        name: 'Wide Column (70%)',
        description:
            'Add one component, e.g. a Carousel with Product Tiles. A Carousel here drops its own side padding and margins.',
        maxComponents: 1,
        // Literal list: the cartridge generator reads decorators statically. Add new content types here.
        componentTypeInclusions: [
            'Content.announcementBanner',
            'Content.contentCard',
            'Content.hero',
            'Content.pdImage',
            'Content.popularCategory',
            'Content.productRecommendations',
            'Content.productTile',
            'priceshoes_content.banner',
            'priceshoes_content.catalogCard',
            'priceshoes_content.ctaCard',
            'priceshoes_content.imageCard',
            'priceshoes_content.splitHero',
            'priceshoes_layout.carousel',
        ],
    },
])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class SplitLayoutMetadata {
    @AttributeDefinition({
        id: 'headerTitle',
        name: 'Header Title',
        description: 'Optional section title, e.g. "Ofertas del mes"',
        type: 'string',
        required: false,
    })
    headerTitle?: string;

    @AttributeDefinition({
        id: 'headerSubtitle',
        name: 'Header Subtitle',
        description: 'Short text next to the header title',
        type: 'string',
        required: false,
    })
    headerSubtitle?: string;

    @AttributeDefinition({
        id: 'headerLinkText',
        name: 'Header Link Text',
        description: 'e.g. "Ver más". Shown only when both text and link are set.',
        type: 'string',
        required: false,
    })
    headerLinkText?: string;

    @AttributeDefinition({ id: 'headerLinkUrl', name: 'Header Link', type: 'url', required: false })
    headerLinkUrl?: string;

    @AttributeDefinition({
        id: 'hideHeaderTitleOnMobile',
        name: 'Hide Header Title on Mobile',
        description: 'Hide the header title on mobile; it stays visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: false,
    })
    hideHeaderTitleOnMobile?: boolean;

    @AttributeDefinition({
        id: 'hideHeaderSubtitleOnMobile',
        name: 'Hide Header Subtitle on Mobile',
        description: 'Hide the header subtitle on mobile; it stays visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: false,
    })
    hideHeaderSubtitleOnMobile?: boolean;

    @AttributeDefinition({
        id: 'hideHeaderLinkOnMobile',
        name: 'Hide Header Link on Mobile',
        description: 'Hide the "Ver más" link on mobile; it stays visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: false,
    })
    hideHeaderLinkOnMobile?: boolean;

    @AttributeDefinition({
        id: 'columnLayout',
        name: 'Column Layout',
        description:
            '30-70: narrow column on the left. 70-30: narrow column on the right. On mobile the left column goes on top.',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with LAYOUTS.
        values: ['30-70', '70-30'],
        defaultValue: '30-70',
    })
    columnLayout?: string;

    @AttributeDefinition({
        id: 'hideOnDesktop',
        name: 'Hide on Desktop',
        description: 'Hide the section on desktop (still visible in Page Designer)',
        type: 'boolean',
        defaultValue: splitLayoutDefaults.hideOnDesktop,
    })
    hideOnDesktop?: boolean;

    @AttributeDefinition({
        id: 'hideOnMobile',
        name: 'Hide on Mobile',
        description: 'Hide the section on mobile (still visible in Page Designer)',
        type: 'boolean',
        defaultValue: splitLayoutDefaults.hideOnMobile,
    })
    hideOnMobile?: boolean;

    @AttributeDefinition({
        id: 'marginTop',
        name: 'Margin Top (px)',
        description: 'Space above the section. Default: 32. Range: 0 to 200.',
        type: 'integer',
        defaultValue: splitLayoutDefaults.marginTop,
    })
    marginTop?: number;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Margin Bottom (px)',
        description: 'Space below the section. Default: 32. Range: 0 to 200.',
        type: 'integer',
        defaultValue: splitLayoutDefaults.marginBottom,
    })
    marginBottom?: number;
}

interface SplitLayoutProps {
    headerTitle?: string;
    headerSubtitle?: string;
    headerLinkText?: string;
    headerLinkUrl?: string;
    hideHeaderTitleOnMobile?: boolean;
    hideHeaderSubtitleOnMobile?: boolean;
    hideHeaderLinkOnMobile?: boolean;
    /** PD enum: "30-70" | "70-30". */
    columnLayout?: string;
    hideOnDesktop?: boolean;
    hideOnMobile?: boolean;
    marginTop?: number;
    marginBottom?: number;
    /** Plain column content (Storybook/test path). Ignored when the PD region has a component. */
    narrow?: RegionItem[];
    wide?: RegionItem[];
    /** Component data containing regions from Page Designer */
    component?: ComponentType;
    className?: string;
}

function clampMargin(value: number | undefined, fallback: number): number {
    const margin = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return Math.min(Math.max(margin, 0), MAX_MARGIN);
}

function isColumnLayout(value: string | undefined): value is ColumnLayout {
    return value !== undefined && Object.hasOwn(LAYOUTS, value);
}

/** The layout pads the section, so a nested carousel drops its own padding and margins. */
function withEmbedded(comp: ComponentType): ComponentType {
    return EMBEDDABLE_TYPES.has(comp.typeId) ? withComponentData(comp, { embedded: true }) : comp;
}

/**
 * PriceShoes split section: section header plus a 30% and a 70%
 * column, each holding one Page Designer component. The merchant picks which side the narrow
 * column goes on. On mobile the columns stack, left one on top.
 */
export default function SplitLayout({
    headerTitle,
    headerSubtitle,
    headerLinkText,
    headerLinkUrl,
    hideHeaderTitleOnMobile = false,
    hideHeaderSubtitleOnMobile = false,
    hideHeaderLinkOnMobile = false,
    columnLayout,
    hideOnDesktop = splitLayoutDefaults.hideOnDesktop,
    hideOnMobile = splitLayoutDefaults.hideOnMobile,
    marginTop,
    marginBottom,
    narrow: propNarrow = NO_REGION_ITEMS,
    wide: propWide = NO_REGION_ITEMS,
    component,
    className,
}: SplitLayoutProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();

    const regions: Record<ColumnId, ReturnType<typeof useRegionItems>> = {
        narrow: useRegionItems({
            component,
            regionId: 'narrow',
            fallback: propNarrow,
            decorate: withEmbedded,
            itemClassName: ITEM_CLASS,
        }),
        wide: useRegionItems({
            component,
            regionId: 'wide',
            fallback: propWide,
            decorate: withEmbedded,
            itemClassName: ITEM_CLASS,
        }),
    };

    if (!isDesignMode && hideOnDesktop && hideOnMobile) return null;

    const layout = LAYOUTS[isColumnLayout(columnLayout) ? columnLayout : splitLayoutDefaults.columnLayout];
    // On the storefront an empty column is dropped; in Page Designer it shows a drop placeholder.
    const columns = layout.order.filter((id) => isDesignMode || regions[id].items.length > 0);
    if (columns.length === 0) return null;

    // In design mode the section stays visible on both breakpoints so it can always be edited.
    const visibilityClass = cn(
        !isDesignMode && hideOnMobile && 'hidden',
        !isDesignMode && (hideOnDesktop ? 'md:hidden' : 'md:block')
    );

    const renderColumn = (id: ColumnId): ReactNode => {
        const { items, wrap } = regions[id];
        return (
            <div key={id} data-column={id} className="flex min-w-0 flex-col">
                {wrap(
                    items.length > 0 ? (
                        items.map((item) => (
                            <div key={item.key} className="flex flex-1 flex-col">
                                {item.content}
                            </div>
                        ))
                    ) : (
                        <div className="flex h-[250px] items-center justify-center rounded-2xl bg-ps-gray-100 text-ps-gray-600">
                            <p>{t('splitLayout.emptyLabel')}</p>
                        </div>
                    )
                )}
            </div>
        );
    };

    return (
        <section
            data-slot="split-layout"
            aria-label={headerTitle?.trim() || undefined}
            style={{
                marginTop: clampMargin(marginTop, splitLayoutDefaults.marginTop),
                marginBottom: clampMargin(marginBottom, splitLayoutDefaults.marginBottom),
            }}
            // 16px side margins on mobile and 32px from md up (Figma).
            className={cn('w-full px-4 md:px-8', visibilityClass, className)}>
            <SectionHeader
                title={headerTitle}
                subtitle={headerSubtitle}
                linkText={headerLinkText}
                linkUrl={headerLinkUrl}
                hideOnMobile={{
                    title: hideHeaderTitleOnMobile,
                    subtitle: hideHeaderSubtitleOnMobile,
                    link: hideHeaderLinkOnMobile,
                }}
                className="mb-4 md:mb-6"
            />
            {/* One row per column on mobile; side by side from md, both stretched to the taller one. */}
            <div
                data-slot="split-layout-columns"
                className={cn('grid grid-cols-1 gap-4 md:gap-6', columns.length === 2 && layout.gridClass)}>
                {columns.map(renderColumn)}
            </div>
        </section>
    );
}
